import { useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    ChatCircleText, NotePencil, PaperPlaneRight, Warning,
} from "@phosphor-icons/react";

/**
 * Composer with two tabs:
 *  - Mensaje al cliente   (real outbound; disabled while SMTP_ENABLED=false or trip not in whitelist)
 *  - Nota interna         (always available; never leaves the Hub)
 *
 * Props:
 *  - onSendMessage(text)
 *  - onAddNote(text)
 *  - disabled               (whole composer; viewer / closed conv)
 *  - sendDisabled           (only the Message tab outbound; e.g. KimKim trip not in whitelist)
 *  - sendDisabledReason     (tooltip text shown on the Send button when disabled)
 *  - requireConfirm         (Phase 2.4: show the "you are sending REAL email" dialog the first time
 *                            in this session; once accepted, subsequent sends skip the dialog)
 *  - confirmTitle / confirmBody (strings for the dialog)
 */
export default function Composer({
    onSendMessage, onAddNote,
    disabled = false,
    sendDisabled = false,
    sendDisabledReason = "Envío real deshabilitado.",
    sendDisabledSeverity = "warning",  // "warning" | "critical"
    requireConfirm = false,
    confirmTitle = "Confirmar envío",
    confirmBody = "",
}) {
    const [mode, setMode] = useState("message"); // "message" | "note"
    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Persist across components by keeping the flag in the module-level ref store.
    // (A single agent session only confirms once across all KimKim conversations.)
    const sessionAckRef = useRef(globalThis.__viajaHubOutboundAck === true);

    const isNote = mode === "note";
    const effectiveDisabled = disabled || (!isNote && sendDisabled);

    const doSend = async (body) => {
        setSubmitting(true);
        try {
            if (isNote) await onAddNote(body);
            else        await onSendMessage(body);
            setText("");
        } finally {
            setSubmitting(false);
        }
    };

    const send = async () => {
        const body = text.trim();
        if (!body || submitting || effectiveDisabled) return;
        if (!isNote && requireConfirm && !sessionAckRef.current) {
            setConfirmOpen(true);
            return;
        }
        await doSend(body);
    };

    const confirmYes = async () => {
        sessionAckRef.current = true;
        globalThis.__viajaHubOutboundAck = true;
        setConfirmOpen(false);
        const body = text.trim();
        if (body) await doSend(body);
    };

    const onKey = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            send();
        }
    };

    const sendBtn = (
        <Button
            onClick={send}
            disabled={!text.trim() || effectiveDisabled || submitting}
            className={`${isNote
                ? "bg-amber-600 hover:bg-amber-700 text-white"
                : "bg-brand-600 hover:bg-brand-700 text-white"} min-h-[44px] px-4 text-sm sm:text-sm`}
            data-testid="composer-submit-btn"
        >
            {submitting
                ? "Enviando…"
                : <><PaperPlaneRight size={16} weight="fill" className="mr-1.5"/>{isNote ? "Guardar nota" : "Enviar"}</>
            }
        </Button>
    );

    return (
        <div className={`border-t border-slate-200 p-3 shrink-0 transition-colors vh-safe-bottom sticky bottom-0 z-10 ${isNote ? "vh-composer-note" : "bg-white"}`}
             data-testid="composer-root">
            <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="grid grid-cols-2 mb-3 bg-slate-100">
                    <TabsTrigger value="message" data-testid="composer-tab-message">
                        <ChatCircleText size={14} className="mr-1.5" />
                        Mensaje al cliente
                    </TabsTrigger>
                    <TabsTrigger value="note" data-testid="composer-tab-note">
                        <NotePencil size={14} className="mr-1.5" />
                        Nota interna
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {!isNote && sendDisabled && (
                <div
                    className={`mb-2 flex items-start gap-2 px-3 py-2 rounded-md border text-xs ${
                        sendDisabledSeverity === "critical"
                            ? "border-rose-300 bg-rose-50 text-rose-900"
                            : "border-amber-200 bg-amber-50 text-amber-900"
                    }`}
                    data-testid="composer-send-disabled-banner"
                    data-severity={sendDisabledSeverity}
                >
                    <Warning size={14} weight="fill" className={`mt-0.5 shrink-0 ${
                        sendDisabledSeverity === "critical" ? "text-rose-700" : "text-amber-700"
                    }`} />
                    <span>
                        {sendDisabledReason}
                        {sendDisabledSeverity !== "critical" && " Los compañeros sí pueden añadir notas internas mientras tanto."}
                    </span>
                </div>
            )}

            <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                rows={3}
                disabled={disabled || submitting}
                autoFocus
                autoCapitalize="sentences"
                autoComplete="off"
                spellCheck="true"
                placeholder={
                    isNote
                        ? "Escribe una nota interna que solo verá el equipo…"
                        : (sendDisabled
                            ? "Redacta el mensaje (no se enviará al cliente)…"
                            : "Escribe tu mensaje al cliente…")
                }
                className={`min-h-[60px] max-h-[200px] resize-y ${isNote ? "bg-white/60 border-yellow-300 focus-visible:ring-yellow-300" : ""}`}
                data-testid="composer-textarea"
            />

            <div className="flex items-center justify-between mt-2 gap-2">
                <span className="text-[11px] text-slate-500 hidden sm:inline">⌘/Ctrl + Enter para enviar</span>
                <span className="sm:hidden" />
                {effectiveDisabled && !isNote ? (
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-block" data-testid="composer-submit-tooltip-wrap">{sendBtn}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{sendDisabledReason}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    sendBtn
                )}
            </div>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="confirm-real-send-dialog">
                    <DialogHeader>
                        <DialogTitle>{confirmTitle}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Es la primera vez en esta sesión. Después no te volveré a preguntar
                            hasta que cierres sesión o recargues la pestaña.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmBody && (
                        <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] whitespace-pre-wrap">{confirmBody}</pre>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-real-send-cancel">
                            Cancelar
                        </Button>
                        <Button onClick={confirmYes} className="bg-brand-600 hover:bg-brand-700"
                                data-testid="confirm-real-send-yes">
                            Sí, enviar mensaje
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
